import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, Phone, Briefcase, FileText, Clock, User, Building2, MapPin, Download, ChevronDown, ChevronUp, Loader2, LogOut, Settings, Save, Check } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const tabs = [
  { key: 'contacts', label: 'Contact Submissions', icon: Mail },
  { key: 'quotes', label: 'Quote Requests', icon: FileText },
  { key: 'applications', label: 'Job Applications', icon: Briefcase },
  { key: 'settings', label: 'Site Settings', icon: Settings },
];

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return dateStr;
  }
};

/* ——— Expandable Row ——— */
const ExpandableCard = ({ children, header, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div
      className="transition-all duration-300 border"
      style={{
        background: open ? '#FAFAF8' : '#FFFFFF',
        borderColor: open ? '#B8A066' : '#E5DFD5',
      }}
    >
      <button
        className="w-full flex items-center justify-between p-5 cursor-pointer text-left bg-transparent border-none"
        onClick={() => setOpen(!open)}
      >
        <div className="flex-1">{header}</div>
        {open ? <ChevronUp size={18} className="text-[#B8A066]" /> : <ChevronDown size={18} className="text-[#5C5855]/50" />}
      </button>
      {open && (
        <div className="px-5 pb-5 pt-0 border-t border-[#E5DFD5]">
          {children}
        </div>
      )}
    </div>
  );
};

/* ——— Contact Card ——— */
const ContactCard = ({ item }) => (
  <ExpandableCard
    header={
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6">
        <div className="flex items-center gap-2">
          <User size={14} className="text-[#B8A066]" />
          <span className="text-[#3D3A38] font-medium text-sm" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{item.name}</span>
        </div>
        <span className="text-[#5C5855] text-xs" style={{ fontFamily: 'Outfit, sans-serif' }}>{item.email}</span>
        {item.service && <span className="text-xs px-2 py-0.5 bg-[#B8A066]/10 text-[#B8A066]" style={{ fontFamily: 'Outfit, sans-serif' }}>{item.service}</span>}
        <span className="text-[#5C5855]/50 text-xs ml-auto" style={{ fontFamily: 'Outfit, sans-serif' }}>{formatDate(item.created_at)}</span>
      </div>
    }
  >
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
      <div className="flex flex-col gap-3">
        {item.company && (
          <div className="flex items-center gap-2">
            <Building2 size={14} className="text-[#5C5855]/50" />
            <span className="text-[#5C5855] text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>{item.company}</span>
          </div>
        )}
        {item.phone && (
          <div className="flex items-center gap-2">
            <Phone size={14} className="text-[#5C5855]/50" />
            <span className="text-[#5C5855] text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>{item.phone}</span>
          </div>
        )}
        {item.address && (
          <div className="flex items-center gap-2">
            <MapPin size={14} className="text-[#5C5855]/50" />
            <span className="text-[#5C5855] text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>{item.address}</span>
          </div>
        )}
        {item.file_name && (
          <div className="flex items-center gap-2">
            <Download size={14} className="text-[#B8A066]" />
            <span className="text-sm text-[#B8A066]" style={{ fontFamily: 'Outfit, sans-serif' }}>Attachment: {item.file_name}</span>
          </div>
        )}
      </div>
      <div>
        <p className="text-[#5C5855]/50 text-xs mb-1 uppercase tracking-wider" style={{ fontFamily: 'Outfit, sans-serif' }}>Message</p>
        <p className="text-[#3D3A38] text-sm leading-relaxed" style={{ fontFamily: 'Outfit, sans-serif' }}>{item.message || '—'}</p>
      </div>
    </div>
  </ExpandableCard>
);

/* ——— Quote Card ——— */
const QuoteCard = ({ item }) => (
  <ExpandableCard
    header={
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6">
        <div className="flex items-center gap-2">
          <FileText size={14} className="text-[#B8A066]" />
          <span className="text-[#3D3A38] font-medium text-sm" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{item.name}</span>
        </div>
        <span className="text-[#5C5855] text-xs" style={{ fontFamily: 'Outfit, sans-serif' }}>{item.email}</span>
        <span className="text-[#5C5855]/50 text-xs ml-auto" style={{ fontFamily: 'Outfit, sans-serif' }}>{formatDate(item.created_at)}</span>
      </div>
    }
  >
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
      <div className="flex flex-col gap-3">
        {item.company && (
          <div className="flex items-center gap-2">
            <Building2 size={14} className="text-[#5C5855]/50" />
            <span className="text-[#5C5855] text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>{item.company}</span>
          </div>
        )}
        {item.phone && (
          <div className="flex items-center gap-2">
            <Phone size={14} className="text-[#5C5855]/50" />
            <span className="text-[#5C5855] text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>{item.phone}</span>
          </div>
        )}
      </div>
      <div>
        <p className="text-[#5C5855]/50 text-xs mb-1 uppercase tracking-wider" style={{ fontFamily: 'Outfit, sans-serif' }}>Message</p>
        <p className="text-[#3D3A38] text-sm leading-relaxed" style={{ fontFamily: 'Outfit, sans-serif' }}>{item.message || '—'}</p>
      </div>
    </div>
  </ExpandableCard>
);

/* ——— Application Card ——— */
const ApplicationCard = ({ item }) => (
  <ExpandableCard
    header={
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6">
        <div className="flex items-center gap-2">
          <Briefcase size={14} className="text-[#B8A066]" />
          <span className="text-[#3D3A38] font-medium text-sm" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{item.name}</span>
        </div>
        <span className="text-[#5C5855] text-xs" style={{ fontFamily: 'Outfit, sans-serif' }}>{item.email}</span>
        {item.trade && <span className="text-xs px-2 py-0.5 bg-[#B8A066]/10 text-[#B8A066]" style={{ fontFamily: 'Outfit, sans-serif' }}>{item.trade}</span>}
        <span className="text-[#5C5855]/50 text-xs ml-auto" style={{ fontFamily: 'Outfit, sans-serif' }}>{formatDate(item.created_at)}</span>
      </div>
    }
  >
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
      <div className="flex flex-col gap-3">
        {item.phone && (
          <div className="flex items-center gap-2">
            <Phone size={14} className="text-[#5C5855]/50" />
            <span className="text-[#5C5855] text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>{item.phone}</span>
          </div>
        )}
        {item.experience && (
          <div className="flex items-center gap-2">
            <Clock size={14} className="text-[#5C5855]/50" />
            <span className="text-[#5C5855] text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>{item.experience}</span>
          </div>
        )}
        {item.resume_filename && (
          <div className="flex items-center gap-2">
            <Download size={14} className="text-[#B8A066]" />
            <span className="text-sm text-[#B8A066]" style={{ fontFamily: 'Outfit, sans-serif' }}>Resume: {item.resume_filename}</span>
          </div>
        )}
      </div>
      <div>
        <p className="text-[#5C5855]/50 text-xs mb-1 uppercase tracking-wider" style={{ fontFamily: 'Outfit, sans-serif' }}>About</p>
        <p className="text-[#3D3A38] text-sm leading-relaxed" style={{ fontFamily: 'Outfit, sans-serif' }}>{item.message || '—'}</p>
      </div>
    </div>
  </ExpandableCard>
);

/* ——— Settings Panel ——— */
const SettingsPanel = ({ token }) => {
  const { settings, refreshSettings } = useSettings();
  const [form, setForm] = useState({ ...settings });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setForm({ ...settings });
  }, [settings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await refreshSettings();
      setSaved(true);
      toast.success('Settings saved successfully');
      setTimeout(() => setSaved(false), 2000);
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    }
    setSaving(false);
  };

  return (
    <div className="space-y-8">
      <div className="p-6 bg-white border border-[#E5DFD5]">
        <h3 className="text-lg text-[#3D3A38] mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Contact Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="form-label">Phone Number</label>
            <input
              type="text"
              value={form.phone || ''}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="form-input-underline"
              placeholder="647-462-0928"
            />
          </div>
          <div>
            <label className="form-label">Email</label>
            <input
              type="email"
              value={form.email || ''}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="form-input-underline"
              placeholder="info@aozatelectrical.ca"
            />
          </div>
          <div className="md:col-span-2">
            <label className="form-label">Office Address</label>
            <input
              type="text"
              value={form.address || ''}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              className="form-input-underline"
              placeholder="2 - 21 Signal Hill Avenue, Toronto, Ontario M9W6V8"
            />
          </div>
        </div>
      </div>

      <div className="p-6 bg-white border border-[#E5DFD5]">
        <h3 className="text-lg text-[#3D3A38] mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Business Hours</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="form-label">Regular Hours</label>
            <input
              type="text"
              value={form.business_hours || ''}
              onChange={(e) => setForm({ ...form, business_hours: e.target.value })}
              className="form-input-underline"
              placeholder="Mon–Fri: 7:00 AM – 5:00 PM"
            />
          </div>
          <div>
            <label className="form-label">Emergency Hours</label>
            <input
              type="text"
              value={form.emergency_hours || ''}
              onChange={(e) => setForm({ ...form, emergency_hours: e.target.value })}
              className="form-input-underline"
              placeholder="Emergency service available 24/7"
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn-primary inline-flex items-center gap-2"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : saved ? <Check size={14} /> : <Save size={14} />}
          {saving ? 'Saving...' : saved ? 'Saved!' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
};

/* ——— Main Admin Page ——— */
export default function AdminPage() {
  const { logout, token } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('contacts');
  const [contacts, setContacts] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [contactsRes, quotesRes, appsRes] = await Promise.all([
          axios.get(`${API}/admin/contacts`, { headers }),
          axios.get(`${API}/admin/quotes`, { headers }),
          axios.get(`${API}/admin/applications`, { headers }),
        ]);
        setContacts(contactsRes.data);
        setQuotes(quotesRes.data);
        setApplications(appsRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
        if (error.response?.status === 401) {
          logout();
          navigate('/admin/login');
        }
      }
      setLoading(false);
    };
    if (token) fetchData();
  }, [token, logout, navigate]);

  const renderContent = () => {
    if (activeTab === 'settings') {
      return <SettingsPanel token={token} />;
    }

    const dataMap = {
      contacts: { data: contacts, Card: ContactCard, empty: 'No contact submissions yet.' },
      quotes: { data: quotes, Card: QuoteCard, empty: 'No quote requests yet.' },
      applications: { data: applications, Card: ApplicationCard, empty: 'No job applications yet.' },
    };
    const { data, Card, empty } = dataMap[activeTab];

    if (loading) {
      return (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={24} className="animate-spin text-[#B8A066]" />
        </div>
      );
    }

    if (data.length === 0) {
      return (
        <div className="text-center py-20 border border-[#E5DFD5]">
          <p className="text-[#5C5855]" style={{ fontFamily: 'Outfit, sans-serif' }}>{empty}</p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {data.map((item, i) => <Card key={item.id || i} item={item} />)}
      </div>
    );
  };

  return (
    <main className="min-h-screen bg-[#F5F1EC]" data-testid="admin-page">
      {/* Header */}
      <header className="bg-[#3D3A38] border-b border-[#B8A066]/20">
        <div className="container-custom py-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-[#F5F1EC]/60 hover:text-[#F5F1EC] transition-colors">
              <ArrowLeft size={20} />
            </Link>
            <div>
              <h1 className="text-xl text-[#F5F1EC]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Admin Dashboard
              </h1>
              <p className="text-xs text-[#F5F1EC]/40" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Aozat Electrical
              </p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-[#F5F1EC]/60 hover:text-[#F5F1EC] transition-colors text-sm"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            <LogOut size={16} />
            Sign Out
          </button>
        </div>
      </header>

      {/* Tabs */}
      <div className="bg-white border-b border-[#E5DFD5]">
        <div className="container-custom">
          <div className="flex gap-1 overflow-x-auto py-1">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-5 py-4 text-xs tracking-[0.1em] uppercase transition-all border-b-2 ${
                  activeTab === tab.key
                    ? 'border-[#B8A066] text-[#3D3A38]'
                    : 'border-transparent text-[#5C5855]/60 hover:text-[#3D3A38]'
                }`}
                style={{ fontFamily: 'Outfit, sans-serif', background: 'none' }}
              >
                <tab.icon size={14} />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container-custom py-8">
        {renderContent()}
      </div>
    </main>
  );
}
